import { EvergreenWealthLogo } from '@egw/assets'
import { BACK_LINK_ID, ModalDrawer, useModalState } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { Bars3Icon, ChevronLeftIcon } from '@heroicons/react/24/outline'
import cx from 'classnames'
import { type FC, useEffect, useMemo, useState } from 'react'
import { matchPath, useLocation, useNavigate } from 'react-router'
import { VerticalNavigation } from '../vertical-navigation'

export interface MobileNavigationProps {
  className?: string
  id?: string
  testId?: string
  ariaLabel?: string
  isChatOpen: boolean
  setIsChatOpen: (_isChatOpen: boolean) => void
}

export const MobileNavigation: FC<MobileNavigationProps> = ({
  className,
  id,
  testId = 'mobile-navigation',
  ariaLabel,
  isChatOpen,
  setIsChatOpen,
}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [currentBackLink, setCurrentBackLink] = useState<string | undefined>(undefined)

  const { isOpen, openModal, closeModal } = useModalState()

  const currentRoute = useMemo(() => {
    return Object.values(WEALTH_ROUTES).find((r) => {
      return matchPath(r.path, pathname)
    })
  }, [pathname])

  useEffect(() => {
    // Query the back link URL from the redesigned page header component
    const backLinkButton = document.getElementById(BACK_LINK_ID)
    const url = backLinkButton?.getAttribute('data-back-link-url')
    setCurrentBackLink(url ?? undefined)
    // run the useEffect again when the pathname changes or backPath changes
  }, [pathname, currentRoute?.backPath])

  const backLinkHandler = (): void => {
    if (typeof document !== 'undefined') {
      // Check if there's a specific back path defined for the current route
      if (currentBackLink) {
        void navigate(currentBackLink)
      } else if (window.history.length > 1) {
        window.history.back()
      } else {
        void navigate(WEALTH_ROUTES.OVERVIEW.path)
      }
    }
  }

  const baseClass = cx('flex lg:hidden items-center justify-between w-full px-4 py-3 bg-white')

  return (
    <div className={cx(baseClass, className)} id={id} data-testid={testId} aria-label={ariaLabel}>
      {/* Back Arrow */}
      <button
        className={cx('p-2 -ml-2 transition-opacity duration-200', {
          'opacity-70 hover:opacity-100': currentRoute?.backPath ?? isChatOpen,
          invisible: !currentRoute?.backPath && !isChatOpen,
        })}
        name="mobile-navigation-back-link"
        data-testid="mobile-nav-back-link"
        type="button"
        onClick={
          isChatOpen
            ? () => {
                setIsChatOpen(false)
              }
            : backLinkHandler
        }
        aria-label="Go back"
      >
        <ChevronLeftIcon className="w-6 h-6 text-text-header" />
      </button>

      {/* Evergreen Wealth Logo/Wordmark */}
      <button
        type="button"
        className="flex-1 flex justify-center px-4"
        data-testid="mobile-nav-logo"
        name="mobile-navigation-logo"
        aria-label="Evergreen Wealth home"
        onClick={() => void navigate(WEALTH_ROUTES.OVERVIEW.path)}
      >
        <EvergreenWealthLogo className="w-[123px] text-text-header" />
      </button>

      {/* Hamburger Menu */}
      <button
        className="p-2 -mr-2 transition-opacity duration-200 opacity-70 hover:opacity-100"
        type="button"
        onClick={openModal}
        data-testid="mobile-nav-menu-button"
        name="mobile-navigation-menu"
        aria-label="Open navigation menu"
      >
        <Bars3Icon className="w-6 h-6 text-text-header" />
      </button>
      {/* Mobile Navigation Modal */}
      <ModalDrawer title="Navigation" isOpen={isOpen} onClose={closeModal} fullBodyContent mobileFullHeight>
        <VerticalNavigation setIsChatOpen={setIsChatOpen} className="w-full rounded-l-[24px]" />
      </ModalDrawer>
    </div>
  )
}
